'use client';

import React from 'react';
import Link, { usePathname } from '@/core/i18n/navigation';
import { cn } from '@/core/utils/cn';
import { BlockProps } from './types';
import { useTranslation } from '@/core/components/providers/TranslationProvider';

export function BreadcrumbsBlock({ block, currentStyles, isAdmin, product, category }: BlockProps) {
  const pathname = usePathname();
  const { t } = useTranslation();

  const DEMO_BREADCRUMBS = [
    { name: t('nav.home'), url: '/' },
    { name: t('nav.products'), url: '/kategoria/demo' },
    { name: 'Przykładowy Produkt', url: '' },
  ];
  const settings = block.settings || {};

  // Custom styling attributes
  const fontSize = currentStyles.fontSize ? `${currentStyles.fontSize}px` : '0.875rem'; // 14px default
  const fontWeight = currentStyles.fontWeight || '400';
  const textColor = currentStyles.textColor || '#64748b'; // slate-500 default
  const linkColor = currentStyles.linkColor || '#475569'; // slate-700 default
  const hoverColor = currentStyles.hoverColor || '#3b82f6'; // primary/blue default
  const activeColor = currentStyles.activeColor || '#0f172a'; // slate-900 default
  const activeFontWeight = currentStyles.activeFontWeight || '500';
  const separatorColor = currentStyles.separatorColor || '#cbd5e1'; // slate-300 default
  const separatorSymbol = settings.separator || '/';
  const gapVal = currentStyles.gap ?? settings.gap;
  const gap = gapVal !== undefined ? `${gapVal}px` : '0.5rem'; // 8px default
  const alignment = currentStyles.justifyContent || 'flex-start';

  // Construct breadcrumbs list
  let breadcrumbs: { name: string; url: string }[] = [];

  if (product) {
    breadcrumbs.push({ name: t('nav.home'), url: '/' });
    if (product.category_path && Array.isArray(product.category_path)) {
      product.category_path.forEach((cat: any, index: number) => {
        const fullPath = product.category_path
          .slice(0, index + 1)
          .map((c: any) => c.slug)
          .join('/');
        breadcrumbs.push({ name: cat.name, url: `/kategoria/${fullPath}` });
      });
    }
    breadcrumbs.push({ name: product.name, url: '' });
  } else if (category) {
    // Use correct database names for category paths, preserving Polish characters
    breadcrumbs.push({ name: t('nav.home'), url: '/' });
    if (category.category_path && Array.isArray(category.category_path)) {
      category.category_path.forEach((cat: any, index: number) => {
        const fullPath = category.category_path
          .slice(0, index + 1)
          .map((c: any) => c.slug)
          .join('/');
        const isLast = index === category.category_path.length - 1;
        breadcrumbs.push({
          name: cat.name,
          url: isLast ? '' : `/kategoria/${fullPath}`,
        });
      });
    } else {
      breadcrumbs.push({ name: category.name, url: '' });
    }
  } else if (isAdmin) {
    breadcrumbs = DEMO_BREADCRUMBS;
  } else {
    // Dynamic generation from pathname for general pages/categories
    breadcrumbs.push({ name: t('nav.home'), url: '/' });
    if (pathname && pathname !== '/') {
      const segments = pathname.split('/').filter(Boolean);
      let currentPath = '';

      // Skip the /kategoria or /produkty root prefix from showing up weirdly,
      // but resolve their children beautifully.
      let skipIndex = -1;
      if (segments[0] === 'kategoria' || segments[0] === 'produkty') {
        skipIndex = 0;
      }

      segments.forEach((seg, index) => {
        currentPath += `/${seg}`;
        if (index === skipIndex) return; // Skip showing "kategoria" or "produkty" as breadcrumb segment

        // Decode slug and make it readable
        let name = decodeURIComponent(seg.replace(/-/g, ' '));
        // Capitalize first letter
        name = name.charAt(0).toUpperCase() + name.slice(1);

        // Adjust link to match standard paths
        let url = currentPath;
        const isLast = index === segments.length - 1;

        breadcrumbs.push({
          name,
          url: isLast ? '' : url,
        });
      });
    }
  }

  // Generate unique class namespace for hover effect styling
  const blockClass = `breadcrumbs-${block.id}`;

  return (
    <div className={cn("w-full relative z-10 block-inner-content flex flex-wrap pb-1")} style={{ justifyContent: alignment }}>
      <style>{`
        .${blockClass} a {
          color: ${linkColor};
          font-weight: ${fontWeight};
          transition: color 150ms;
        }
        .${blockClass} a:hover {
          color: ${hoverColor};
        }
        .${blockClass} span.separator {
          color: ${separatorColor};
        }
        .${blockClass} span.active {
          color: ${activeColor};
          font-weight: ${activeFontWeight};
        }
        @media (max-width: 640px) {
          .${blockClass} li:not(:first-child):not(:last-child) {
            display: none;
          }
          .${blockClass} li:last-child .active {
            display: block;
            max-width: min(68vw, 280px);
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
        }
      `}</style>
      
      <nav aria-label="Breadcrumb" className={cn(blockClass, "flex flex-wrap py-2")} style={{ fontSize }}>
        <ol className="flex flex-wrap items-center" style={{ gap }}>
          {breadcrumbs.map((crumb, idx) => {
            const isLast = idx === breadcrumbs.length - 1;

            return (
              <li key={idx} className="flex items-center min-w-0">
                {idx > 0 && (
                  <span className="separator px-1 select-none font-normal" style={{ color: separatorColor }}>
                    {separatorSymbol}
                  </span>
                )}
                {isLast || !crumb.url ? (
                  <span className="active" title={crumb.name} aria-current={isLast ? 'page' : undefined}>
                    {crumb.name}
                  </span>
                ) : (
                  <Link href={crumb.url} className="hover:underline transition-colors">
                    {crumb.name}
                  </Link>
                )}
              </li>
            );
          })}
        </ol>
      </nav>
    </div>
  );
}
